import { CHANGE_TONES, CURRENT_PERIOD_NAMES, NOT_APPLICABLE } from './dashboardConstants.js';
import { formatRange } from './dashboardDateRange.js';

/**
 * パフォーマンスダッシュボードの表示の書式（SCR-P-001 §4.2「数値の桁」「前期比」・C10）
 *
 * 率はサーバが小数 1 桁に丸めて返す（集計 API 設計 §2.5）。画面は丸め直さず、桁の表記だけを揃える。
 */

/** 前期比の値の種類 */
export const CHANGE_KINDS = {
    COUNT: 'count', // 件数の差（件）
    RATE: 'rate', // 率のポイントの差（pt）
};

/** 良し悪しの向き（C10: 平均発話比率は色を付けない） */
export const CHANGE_DIRECTIONS = {
    HIGHER_IS_BETTER: 'higherIsBetter',
    NONE: 'none',
};

const isNumber = (v) => typeof v === 'number' && Number.isFinite(v);

/** 件数を 3 桁区切りで出す。値が無いときは「—」 */
export function formatCount(value) {
    return isNumber(value) ? value.toLocaleString('ja-JP') : '—';
}

/** 率を小数 1 桁の % で出す。null（分母 0）は「N/A」（SCR-P-001 §5.2 #2） */
export function formatRate(value) {
    return isNumber(value) ? `${value.toFixed(1)}%` : NOT_APPLICABLE;
}

/**
 * 前期比の表示（SCR-P-001 C10）
 *
 * @param {number|null|undefined} value - 今 − 前（集計 API 設計 §4.1 の change）。null は比べられない
 * @param {string} kind - CHANGE_KINDS
 * @param {string} direction - CHANGE_DIRECTIONS
 * @returns {{text: string, spokenText: string, tone: string}}
 */
export function formatChange(value, kind, direction) {
    if (!isNumber(value)) {
        return { text: '前期比 —', spokenText: '前期と比べられません', tone: CHANGE_TONES.NEUTRAL };
    }
    if (value === 0) {
        return { text: '前期比 ±0', spokenText: '前期と同じ', tone: CHANGE_TONES.NEUTRAL };
    }

    const increased = value > 0;
    const abs = Math.abs(value);
    const amount = kind === CHANGE_KINDS.RATE ? abs.toFixed(1) : abs.toLocaleString('ja-JP');
    const unit = kind === CHANGE_KINDS.RATE ? 'pt' : '件';
    const spokenUnit = kind === CHANGE_KINDS.RATE ? 'ポイント' : '件';
    const spokenVerb = kind === CHANGE_KINDS.RATE
        ? (increased ? '上昇' : '低下')
        : (increased ? '増加' : '減少');

    let tone = CHANGE_TONES.NEUTRAL;
    if (direction === CHANGE_DIRECTIONS.HIGHER_IS_BETTER) {
        tone = increased ? CHANGE_TONES.GOOD : CHANGE_TONES.BAD;
    }

    return {
        text: `前期比 ${increased ? '▲' : '▼'} ${amount} ${unit}`,
        spokenText: `前期より ${amount} ${spokenUnit}${spokenVerb}`,
        tone,
    };
}

/** JST は UTC から固定 +9 時間（日本に夏時間は無い。ブラウザのタイムゾーンに依らずに出すため） */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * ISO 8601 の時刻を JST の年月日・時分に分ける。読めない値は null
 *
 * @param {string} iso - 例 "2026-10-21T14:30:00+09:00"
 * @returns {{year: number, month: number, day: number, hour: number, minute: number}|null}
 */
export function toJstParts(iso) {
    const ms = typeof iso === 'string' ? Date.parse(iso) : NaN;
    if (Number.isNaN(ms)) return null;
    const jst = new Date(ms + JST_OFFSET_MS);
    return {
        year: jst.getUTCFullYear(),
        month: jst.getUTCMonth() + 1,
        day: jst.getUTCDate(),
        hour: jst.getUTCHours(),
        minute: jst.getUTCMinutes(),
    };
}

/**
 * 集計時点と、今の期間が途中までの値であることの注記（DP-P-013・C10。#1958 の決定 D8）
 * 例「2026/10/22 15:30 時点（今月は 10/22 までの値）」
 *
 * 期間の範囲を指定しているときは「2026/10/29 10:00 時点（2026/10/01〜2026/10/29 の値）」とし、
 * 今の期間が途中までの注記は出さない（範囲の表示が代わりになる。§6.5 33）
 *
 * @param {string} generatedAt - 応答の generated_at
 * @param {string} period - CD-005
 * @param {{from: string, to: string}|null} [range]
 * @returns {string|null} 時刻が読めないときは null
 */
export function formatGeneratedAtNote(generatedAt, period, range = null) {
    const p = toJstParts(generatedAt);
    if (!p) return null;
    const stamp = `${p.year}/${pad2(p.month)}/${pad2(p.day)} ${pad2(p.hour)}:${pad2(p.minute)} 時点`;
    if (range) return `${stamp}（${formatRange(range)} の値）`;
    const currentName = CURRENT_PERIOD_NAMES[period];
    return currentName ? `${stamp}（${currentName}は ${p.month}/${p.day} までの値）` : stamp;
}
